import React, { useRef, useEffect, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import Chart from 'chart.js/auto';

// Helper function to convert table HTML to CSV
const tableToCSV = (tableElement: HTMLTableElement) => {
  const rows = tableElement.querySelectorAll('tr');
  const csvContent: string[] = [];

  rows.forEach(row => {
    const cells = row.querySelectorAll('th, td');
    const rowData = Array.from(cells).map(cell => {
      const content = (cell as HTMLElement).getAttribute('data-raw-value') ||
                     (cell as HTMLElement).textContent?.replace(/"/g, '""') || '';
      return `"${content}"`;
    });
    csvContent.push(rowData.join(','));
  });

  return csvContent.join('\n');
};

// Helper function to handle CSV download
const downloadTableAsCSV = (tableElement: HTMLTableElement, fileName = 'table-data.csv') => {
  const csv = tableToCSV(tableElement);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.setAttribute('download', fileName);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

interface DiMarkdownProps {
  children: string;
  showTableDownloadButton?: boolean;
}

const ChartComponent: React.FC<{ content: string }> = ({ content }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const chartInstanceRef = useRef<Chart | null>(null);

  useEffect(() => {
    if (canvasRef.current) {
      try {
        const chartData = JSON.parse(content);
        if (chartInstanceRef.current) {
          chartInstanceRef.current.destroy();
        }

        chartInstanceRef.current = new Chart(canvasRef.current, {
          type: chartData.type || 'line',
          data: chartData.data,
          options: chartData.options || {}
        });
      } catch (error) {
        console.error('Failed to parse chart data:', error);
      }
    }
    return () => {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
      }
    };
  }, [content]);

  return <canvas ref={canvasRef} style={{ marginBottom: '16px' }} />;
};

const TableComponent: React.FC<{
  children: React.ReactNode;
  showDownloadButton?: boolean;
}> = ({ children, showDownloadButton = true }) => {
  const tableRef = useRef<HTMLTableElement>(null);

  const handleDownload = () => {
    if (tableRef.current) {
      downloadTableAsCSV(tableRef.current);
    }
  };

  return (
    <div style={{ marginBottom: '16px' }}>
      <div className="table-responsive-wrapper">
        <table ref={tableRef}>
          {children}
        </table>
      </div>
      {showDownloadButton && (
        <button className="table-download-btn" onClick={handleDownload}>
          <svg className="MuiSvgIcon-root MuiSvgIcon-fontSizeSmall" focusable="false" aria-hidden="true" viewBox="0 0 24 24" style={{ width: '16px', height: '16px' }}>
            <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z"></path>
          </svg>
          Download CSV
        </button>
      )}
    </div>
  );
};

// Custom Cell component to handle ticker links
const TableCellComponent: React.FC<{ children: React.ReactNode; isHeader?: boolean; index?: number }> = ({ children, isHeader }) => {
  const content = React.Children.toArray(children).join('').trim();
  const isTicker = content && /^[A-Z]{1,5}$/.test(content); // Simple heuristic for tickers

  if (!isHeader && isTicker) {
    return (
      <td data-raw-value={content}>
        <a href={`https://decodeinvesting.com/ticker/${content}`} target="_blank" rel="noopener noreferrer">
          {content}
        </a>
      </td>
    );
  }

  return isHeader ? <th>{children}</th> : <td>{children}</td>;
};

const DiMarkdown: React.FC<DiMarkdownProps> = ({ children, showTableDownloadButton = true }) => {
  const components = useMemo(() => ({
    table: ({ children }: any) => (
      <TableComponent showDownloadButton={showTableDownloadButton}>
        {children}
      </TableComponent>
    ),
    td: ({ children }: any) => <TableCellComponent>{children}</TableCellComponent>,
    th: ({ children }: any) => <TableCellComponent isHeader>{children}</TableCellComponent>,
    code: ({ inline, className, children, ...props }: any) => {
      const match = /language-chart/.exec(className || '');
      if (!inline && match) {
        return <ChartComponent content={String(children).replace(/\n$/, '')} />;
      }
      return <code className={className} {...props}>{children}</code>;
    },
    a: ({ node, ...props }: any) => (
      <a {...props} target="_blank" rel="noopener noreferrer" />
    ),
  }), [showTableDownloadButton]);

  return (
    <ErrorBoundary fallback={<div>Error rendering markdown content</div>}>
      <style>
        {`
          /* Light theme styles */
          :root {
            --color-canvas-default: #ffffff;
            --color-canvas-subtle: #f6f8fa;
            --color-border-default: #d0d7de;
            --color-border-muted: #eaecef;
            --color-stripe: #f8f9fa;
            --color-header-bg: #f1f3f5;
          }

          .table-responsive-wrapper {
            width: 100%;
            overflow-x: auto;
            -webkit-overflow-scrolling: touch;
            margin-bottom: 8px;
          }

          table {
            border-spacing: 0;
            border-collapse: collapse;
            width: 100%;
            margin-bottom: 0;
            font-size: 13px;
            border: 1px solid var(--color-border-muted);
          }

          tr {
            background-color: var(--color-canvas-default);
            border-bottom: 1px solid var(--color-border-muted);
          }

          tbody tr:nth-child(even) {
            background-color: var(--color-stripe);
          }

          thead tr {
            background-color: var(--color-header-bg) !important;
          }

          td, th {
            padding: 6px 12px;
            text-align: left;
            border-right: 1px solid var(--color-border-muted);
          }

          td:last-child, th:last-child {
            border-right: none;
          }

          th {
            font-weight: 500;
            color: #333;
          }

          td {
            color: #555;
          }

          .table-download-btn {
            background-color: #fff;
            border: 1px solid #dee2e6;
            border-radius: 4px;
            padding: 6px 12px;
            margin-top: 4px;
            margin-bottom: 4px;
            cursor: pointer;
            display: inline-flex;
            align-items: center;
            gap: 8px;
            font-size: 13px;
            color: #666;
            transition: all 0.2s ease;
          }

          .table-download-btn:hover {
            background-color: #f8f9fa;
            border-color: #ced4da;
            color: #333;
          }

          .dark-mode {
            --color-canvas-default: #222328;
            --color-canvas-subtle: #1a191d;
            --color-border-default: #322f2f;
            --color-border-muted: #322f2f;
            --color-stripe: rgba(255, 255, 255, 0.02);
            --color-header-bg: #151519;
          }

          .dark-mode th {
            color: #ffffff;
          }

          .dark-mode td {
            color: #d8d8d8;
          }

          .dark-mode .table-download-btn {
            background-color: #151519;
            border-color: #322f2f;
            color: #9c9fa6;
          }

          .dark-mode .table-download-btn:hover {
            background-color: #222328;
            border-color: #9c9fa6;
            color: #ffffff;
          }
        `}
      </style>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={components as any}
      >
        {String(children || '')}
      </ReactMarkdown>
    </ErrorBoundary>
  );
};

class ErrorBoundary extends React.Component<
  { children: React.ReactNode; fallback: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode; fallback: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: any, errorInfo: any) {
    console.error('DiMarkdown crash:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback;
    }
    return this.props.children;
  }
}

export default DiMarkdown;
